import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PowerSettingsNew as PowerIcon
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'
import B2bTDPAPI, { ITDPStausUpdateBackendData } from 'api/b2b/TdpApi'
import OrbitSession from 'hooks/useCookies'

interface CompaniesTableProps {
  isLoading?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[]
  onRefresh?: () => void
}

function CompaniesTable({ isLoading = false, data = [] }: CompaniesTableProps) {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRowId, setSelectedRowId] = useState(null)
  const navigate = useNavigate()
  const faberToken = OrbitSession.get('faberToken')
  const queryClient = useQueryClient()

  // Get the selected row data
  const selectedRow = useMemo(
    () => data.find((row) => row.id === selectedRowId),
    [data, selectedRowId]
  )

  // Mutation for update status of tenant
  const { mutate, isLoading: isPending } = useMutation({
    mutationFn: async (data: ITDPStausUpdateBackendData) => {
      return B2bTDPAPI.updateTDPStatus(data, faberToken)
    },
    onMutate: async (updateData) => {
      const queryKey = ['tdpDetails']
      await queryClient.cancelQueries({ queryKey })
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      const previousData = queryClient.getQueryData<any[]>(queryKey)

      // Optimistically update the cached list
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData<any[]>(queryKey, (old = []) =>
        old.map((item) =>
          item.id === updateData.id
            ? {
                ...item,
                is_active: updateData.status
              }
            : item
        )
      )

      return { previousData }
    },
    onSuccess: () => {
      toast.success('Status updated successfully', {
        hideProgressBar: false,
        autoClose: 2000
      })

      // Invalidate and refetch the data
      queryClient.invalidateQueries({
        queryKey: ['tdpDetails']
      })
      queryClient.invalidateQueries({
        queryKey: ['tenant-search']
      })
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(['tdpDetails'], context.previousData)
      }
      toast.error('Failed to update status', {
        autoClose: 2000,
        hideProgressBar: false
      })
    },
    onSettled: () => {
      handleMenuClose()
    }
  })

  const handleMenuClick = (event, rowId) => {
    setAnchorEl(event.currentTarget)
    setSelectedRowId(rowId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedRowId(null)
  }

  const handleEdit = () => {
    navigate(`/hq/b2b/tdp/edit?id=${selectedRowId}`)
    handleMenuClose()
  }

  const handleToggleActive = () => {
    if (!selectedRow) return

    const updateData = {
      id: selectedRow.id,
      updated_by: OrbitSession.get('email') || '',
      status: !selectedRow.is_active
    }

    mutate(updateData)
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'companyName',
        header: 'Company Name',
        cell: (info) => (
          <Typography
            variant='body2'
            fontWeight='medium'
            sx={{ minWidth: 120 }}
          >
            {info.getValue()}
          </Typography>
        ),
        size: 150
      },
      {
        accessorKey: 'contactPersonName',
        header: 'Contact Person',
        cell: (info) => (
          <Typography variant='body2' sx={{ minWidth: 120 }}>
            {info.getValue()}
          </Typography>
        ),
        size: 150
      },
      {
        accessorKey: 'country',
        header: 'Country',
        cell: (info) => (
          <Typography variant='body2'>{info.getValue()}</Typography>
        ),
        size: 100
      },
      {
        accessorKey: 'city',
        header: 'City',
        cell: (info) => (
          <Typography variant='body2'>{info.getValue()}</Typography>
        ),
        size: 100
      },
      {
        accessorKey: 'currency',
        header: 'Currency',
        cell: (info) => (
          <Chip
            label={info.getValue()}
            size='small'
            variant='outlined'
            sx={{
              fontWeight: 500,
              borderRadius: 1.5
            }}
          />
        ),
        size: 90
      },
      {
        accessorKey: 'businessType',
        header: 'Business Type',
        cell: (info) => (
          <Typography variant='body2' sx={{ minWidth: 100 }}>
            {info.getValue()}
          </Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'referralSource',
        header: 'Referral Source',
        cell: (info) => (
          <Typography variant='body2'>{info.getValue()}</Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'commercialType',
        header: 'Commercial Type',
        cell: (info) => (
          <Typography variant='body2'>{info.getValue()}</Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'accountManager',
        header: 'Account Manager',
        cell: (info) => (
          <Typography variant='body2' sx={{ minWidth: 120 }}>
            {info.getValue()}
          </Typography>
        ),
        size: 140
      },
      {
        accessorKey: 'id',
        header: 'Company ID',
        cell: (info) => (
          <Typography
            variant='body2'
            fontFamily='monospace'
            sx={{
              backgroundColor: 'grey.100',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem'
            }}
          >
            {info.getValue()}
          </Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        cell: (info) => (
          <Chip
            label={info.getValue() ? 'Active' : 'Inactive'}
            color={info.getValue() ? 'success' : 'error'}
            size='small'
            variant='filled'
            sx={{
              fontWeight: 600,
              minWidth: 75
            }}
          />
        ),
        size: 100
      },
      {
        accessorKey: 'status',
        header: 'Registration Status',
        cell: (info) => (
          <Chip
            label={info.getValue()}
            variant='outlined'
            size='small'
            sx={{
              textTransform: 'capitalize',
              fontWeight: 500
            }}
          />
        ),
        size: 140
      },
      {
        accessorKey: 'createdBy',
        header: 'Created By',
        cell: (info) => (
          <Typography variant='body2' color='text.secondary'>
            {info.getValue()}
          </Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'createdOn',
        header: 'Created On',
        cell: (info) => (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ whiteSpace: 'nowrap' }}
          >
            {new Date(info.getValue()).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </Typography>
        ),
        size: 110
      },
      {
        accessorKey: 'updatedBy',
        header: 'Updated By',
        cell: (info) => (
          <Typography variant='body2' color='text.secondary'>
            {info.getValue()}
          </Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'updatedOn',
        header: 'Updated On',
        cell: (info) => (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ whiteSpace: 'nowrap' }}
          >
            {new Date(info.getValue()).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </Typography>
        ),
        size: 110
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <IconButton
            size='small'
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'primary.50'
              }
            }}
            onClick={(event) => handleMenuClick(event, info.row.original.id)}
          >
            <MoreVertIcon fontSize='small' />
          </IconButton>
        ),
        size: 80,
        enableSorting: false
      }
    ],
    []
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination
    }
  })

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mx: 3
        }}
      >
        <Table sx={{ minWidth: 1200 }}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: 'grey.50',
                      py: 2,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                      borderBottom: '2px solid',
                      borderColor: 'divider'
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ textAlign: 'center', py: 8 }}
                >
                  <CircularProgress size={48} sx={{ mb: 2 }} />
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{ mt: 2 }}
                  >
                    Loading companies data...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ textAlign: 'center', py: 8 }}
                >
                  <Typography
                    variant='h6'
                    color='text.secondary'
                    sx={{ mb: 1 }}
                  >
                    No companies found
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Try adjusting your search criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'grey.25'
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        py: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Enhanced Pagination Controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.25'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Showing{' '}
              <strong>
                {table.getState().pagination.pageIndex *
                  table.getState().pagination.pageSize +
                  1}
              </strong>{' '}
              to{' '}
              <strong>
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                  table.getPreFilteredRowModel().rows.length
                )}
              </strong>{' '}
              of <strong>{table.getPreFilteredRowModel().rows.length}</strong>{' '}
              entries
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2'>Rows per page:</Typography>
              <FormControl size='small' sx={{ minWidth: 70 }}>
                <Select
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value))
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      py: 0.75
                    }
                  }}
                >
                  {[10, 20, 30, 40, 50].map((pageSize) => (
                    <MenuItem key={pageSize} value={pageSize}>
                      {pageSize}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                variant='outlined'
                size='small'
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                sx={{ minWidth: 36, px: 1 }}
              >
                {'<<'}
              </Button>
              <Button
                variant='outlined'
                size='small'
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                sx={{ minWidth: 36, px: 1 }}
              >
                {'<'}
              </Button>

              <Typography
                variant='body2'
                sx={{
                  mx: 2,
                  px: 2,
                  py: 0.5,
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </Typography>

              <Button
                variant='outlined'
                size='small'
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                sx={{ minWidth: 36, px: 1 }}
              >
                {'>'}
              </Button>
              <Button
                variant='outlined'
                size='small'
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                sx={{ minWidth: 36, px: 1 }}
              >
                {'>>'}
              </Button>
            </Box>
          </Box>
        </Box>
      </TableContainer>

      {/* Enhanced Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        // PaperProps={{
        //   elevation: 8,
        //   sx: {
        //     borderRadius: 2,
        //     minWidth: 160,
        //     border: '1px solid',
        //     borderColor: 'divider'
        //   }
        // }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            py: 1.5,
            '&:hover': {
              backgroundColor: 'primary.50'
            }
          }}
        >
          <EditIcon fontSize='small' sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant='body2'>Edit Details</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleToggleActive}
          disabled={isPending}
          sx={{
            py: 1.5,
            '&:hover': {
              backgroundColor: selectedRow?.is_active
                ? 'error.50'
                : 'success.50'
            }
          }}
        >
          {isPending ? (
            <CircularProgress size={16} sx={{ mr: 1.5 }} />
          ) : (
            <PowerIcon
              fontSize='small'
              sx={{
                mr: 1.5,
                color: selectedRow?.is_active ? 'error.main' : 'success.main'
              }}
            />
          )}
          <Typography variant='body2'>
            {selectedRow?.is_active ? 'Deactivate' : 'Activate'}
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default CompaniesTable
